import React from 'react';
import withLocalization from '../../higherOrderComponents/withLocalization';
import { getRandomAmount, removeArrayObjectDuplicates } from '../../../common/utils';
import { ActionElement } from '../../elements/ActionElement';
import { TwoDimensionalDataTable } from '../TwoDimensionalDataTable';
import { allowedTimeSlotScheduleProps } from './_allowedPropTypes';

const pseudoTimeScheduleColumns = 4;
const minPseudoTimeSlots = 1;
const maxPseudoTimeSlots = 4;
const pseudoTimeSchedule = new Array(pseudoTimeScheduleColumns).fill().map((_, columnIndex) => {
  const timeSlotsLength = Math.round(getRandomAmount(minPseudoTimeSlots, maxPseudoTimeSlots));

  return {
    columnId: `column-id-${columnIndex}`,
    columnName: `column-name-${columnIndex}`,
    timeSlots: new Array(timeSlotsLength).fill().map((__, timeSlotIndex) => {
      const availabilityId = `pseudo-id-${timeSlotIndex}`;
      const isAvailable = Math.round(getRandomAmount(0, maxPseudoTimeSlots)) > maxPseudoTimeSlots / 2;
      const startsAt = new Date();
      const endsAt = new Date();

      startsAt.setHours(timeSlotIndex + 11, 0, 0, 0);
      endsAt.setHours(startsAt.getHours() + 1);

      return {
        availabilityId,
        isAvailable,
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString()
      };
    })
  };
});

const getRowId = function (startDate, endDate) {
  return [startDate, endDate]
    .map(dateString => new Date(dateString).toISOString())
    .join(';');
};

const sortRowsByDate = function (rowDataA, rowDataB) {
  const {
    startsAt: startDateStringA,
    endsAt: endDateStringA
  } = rowDataA;

  const {
    startsAt: startDateStringB,
    endsAt: endDateStringB
  } = rowDataB;

  const dateStartA = new Date(startDateStringA);
  const dateStartB = new Date(startDateStringB);
  const dateEndA = new Date(endDateStringA);
  const dateEndB = new Date(endDateStringB);

  const allDates = [dateStartA, dateStartB, dateEndA, dateEndB];
  if (!allDates.every(date => !Number.isNaN(date.getTime()))) {
    throw new Error(
      'Invalid TimeSlot data found while sorting: '
      + "both object's startsAt and endsAt props are expected to be a date string, "
      + `but got: "${allDates.map(date => date.toISOString()).join('", "')}"`
    );
  }

  switch (true) {
    case dateStartA < dateStartB:
      return -1;

    case dateStartA > dateStartB:
      return 1;

    case dateEndA < dateEndB:
      return -1;

    case dateEndA > dateEndB:
      return 1;

    default:
      return 0;
  }
};

const getProcessedData = function (props) {
  const {
    formatDateTime,
    scheduleDate,
    scheduleData: passedData,
    dateTimeFormat = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    },
    getCellContentRenderer: passedCellContentRenderer = ({ isAvailable }) => ({ classNames = [] }) => {
      const actionElementProps = {
        type: 'pseudo',
        appearance: isAvailable ? 'button' : 'buttonOutline',
        buttonColor: 'gray',
        className: classNames.join(' ')
      };

      return <ActionElement {...actionElementProps} />;
    }
  } = props;

  const rowHeadings = [];
  const columnHeadings = [];
  const cells = [];
  const availableDateTimes = [];
  const scheduleData = passedData || pseudoTimeSchedule;

  const shouldDisplayDate = function (timeSlotDate) {
    const dayTimeSlot = new Date(timeSlotDate);
    const daySchedule = new Date(scheduleDate);

    dayTimeSlot.setHours(0, 0, 0, 0);
    daySchedule.setHours(0, 0, 0, 0);

    return dayTimeSlot.getTime() === daySchedule.getTime();
  };

  scheduleData.forEach(({ columnId, columnName, timeSlots }) => {
    // Collect column headings:
    columnHeadings.push({
      columnId,
      columnHeadingContent: passedData ? columnName : <div className='placeholder-term' />
    });

    timeSlots.forEach(timeSlotData => {
      const {
        availabilityId,
        startsAt,
        endsAt
      } = timeSlotData;

      if (shouldDisplayDate(startsAt)) {
        availableDateTimes.push({ availabilityId, startsAt, endsAt }); // (array will contain duplicates)

        // Collect cells:
        cells.push({
          renderCellContent: passedCellContentRenderer({
            ...timeSlotData,
            columnName
          }),
          headers: {
            rowId: getRowId(startsAt, endsAt),
            columnId
          }
        });
      }
    });
  });

  // Collect row headings:
  removeArrayObjectDuplicates(availableDateTimes, (a, b) => a.startsAt === b.startsAt && a.endsAt === b.endsAt)
    .sort(sortRowsByDate)
    .forEach(({ startsAt, endsAt }) => {
      const dateBoundaries = { startsAt, endsAt };

      if (shouldDisplayDate(startsAt)) {
        rowHeadings.push({
          rowId: getRowId(startsAt, endsAt),
          rowHeadingContent: !passedData ? <div className='placeholder-term' /> : (
            <span className='no-linebreak'>
              {Object.values(dateBoundaries).map(dateBoundary => (
                <span className='timeSlot-schedule-time'>
                  {formatDateTime(new Date(dateBoundary).toISOString(), dateTimeFormat)}
                </span>
              ))}
            </span>
          )
        });
      }
    });

  return {
    columnHeadings,
    rowHeadings,
    cells
  };
};

const TimeSlotSchedule = props => {
  const {
    classNames: passedClassNames = [],
    scheduleData,
    noResultsContent
  } = props;

  const { rowHeadings, columnHeadings, cells } = getProcessedData(props);
  const classNames = ['timeSlot-schedule', ...passedClassNames];

  if (!scheduleData) {
    classNames.push('no-data');
  }

  return (
    <TwoDimensionalDataTable
      noResultsContent={noResultsContent}
      classNames={classNames}
      rowHeadings={rowHeadings}
      columnHeadings={columnHeadings}
      cells={cells}
    />
  );
};

TimeSlotSchedule.propTypes = allowedTimeSlotScheduleProps;

export default withLocalization(TimeSlotSchedule, []);